import ExpandMore from "@mui/icons-material/ExpandMore";
import { Box, Button, MenuItem, Paper, Stack, TextField } from "@mui/material";
import { Controller, useForm } from "react-hook-form";

import SimplePaperWrapper from "../../../common/components/item/SimplePaperWrapper";
import StyledLoadingButton from "../../../common/providers/theme/design-tokens/LoadingButton/StyledLoadingButton";
import {
  EMISSION_TYPES,
  EQUIPMENT_CLASSIFICATIONS,
  SOURCES,
} from "../../data-hub/constants";

export type EditInspectionReportFormValues = {
  equipmentClassification?: string;
  source?: string;
  emissionType?: string;
  summary?: string;
};

interface EditInspectionReportFormProps {
  defaultValues: EditInspectionReportFormValues;
  onSubmit: (data: EditInspectionReportFormValues) => void;
  onCancel: () => void;
}

const EditInspectionReportForm = ({
  defaultValues,
  onSubmit,
  onCancel,
}: EditInspectionReportFormProps) => {
  const { handleSubmit, control } = useForm<EditInspectionReportFormValues>({
    defaultValues: {
      ...defaultValues,
      emissionType: defaultValues.emissionType || "Fugitive",
    },
  });

  return (
    <Paper
      component="form"
      elevation={0}
      onSubmit={handleSubmit(onSubmit)}
      sx={{
        width: "100%",
        maxWidth: 790,
        borderRadius: "0.75rem",
      }}
    >
      <SimplePaperWrapper
        sx={{
          px: "1.5rem",
          pt: "1rem",
          pb: "1.5rem",
          display: "flex",
          flexDirection: "column",
          gap: "1rem",
          borderTop: theme =>
            `1px solid ${theme.palette.otherOutlineBorder.main}`,
          borderLeft: theme =>
            `1px solid ${theme.palette.otherOutlineBorder.main}`,
          borderRight: theme =>
            `1px solid ${theme.palette.otherOutlineBorder.main}`,
          borderTopLeftRadius: "0.75rem",
          borderTopRightRadius: "0.75rem",
        }}
      >
        <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
          <Controller
            name="equipmentClassification"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                select
                fullWidth
                label="Equipment classification"
                placeholder="Select classification"
                SelectProps={{
                  IconComponent: ExpandMore,
                }}
              >
                {EQUIPMENT_CLASSIFICATIONS.map(item => (
                  <MenuItem key={item} value={item}>
                    {item}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />

          <Controller
            name="source"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                select
                fullWidth
                label="Source"
                placeholder="Select source"
                SelectProps={{
                  IconComponent: ExpandMore,
                }}
              >
                {SOURCES.map(item => (
                  <MenuItem key={item} value={item}>
                    {item}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </Stack>

        <Controller
          name="emissionType"
          control={control}
          rules={{ required: "Emission Type is required" }}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              select
              fullWidth
              required
              label="Emission Type"
              placeholder="Select emission type"
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              SelectProps={{ IconComponent: ExpandMore }}
            >
              {EMISSION_TYPES.map(item => (
                <MenuItem key={item} value={item}>
                  {item}
                </MenuItem>
              ))}
            </TextField>
          )}
        />

        <Controller
          name="summary"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label="Summary"
              fullWidth
              multiline
              minRows={4}
              placeholder="Enter summary"
            />
          )}
        />
      </SimplePaperWrapper>

      <Box
        sx={{
          border: theme => `1px solid ${theme.palette.otherOutlineBorder.main}`,
          borderBottomLeftRadius: "0.75rem",
          borderBottomRightRadius: "0.75rem",
          px: "1.5rem",
          py: "1.5rem",
          display: "flex",
          justifyContent: "flex-end",
          gap: "0.75rem",
        }}
      >
        <Button
          sx={{ width: "160px" }}
          variant="outlined"
          color="secondary"
          onClick={onCancel}
        >
          Cancel
        </Button>
        <StyledLoadingButton
          sx={{ width: "160px" }}
          type="submit"
          variant="contained"
          color="primary"
        >
          Save changes
        </StyledLoadingButton>
      </Box>
    </Paper>
  );
};

export default EditInspectionReportForm;
